import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CrmMessage } from '../models/crm-message.model';
import { SmsSendService } from './sms-send.service';

export interface SmsChatRequest {
  phoneNumber: string;
  contactName: string | null;
  contactSubtitle: string | null;
}

export type SmsStatusTone = 'pending' | 'sent' | 'delivered' | 'failed' | 'received';

/**
 * Opens/closes the admin panel's floating SMS chat (SmsChatConsoleComponent,
 * mounted once in AppComponent - same pattern as the Call console) from any
 * CRM record's SMS tab, and owns the shared per-message helpers both views
 * use: delivery status (checked against Ringotel once per sent message and
 * cached app-wide), labels, segment counts.
 */
@Injectable({
  providedIn: 'root'
})
export class SmsChatService {
  private readonly openSubject = new BehaviorSubject<SmsChatRequest | null>(null);
  readonly open$ = this.openSubject.asObservable();

  /** Digits of the number whose conversation is open and on screen (not
   *  minimised) - the new-message banner skips alerts for it. */
  visibleNumber: string | null = null;

  /** CrmMessage.id -> 'delivered', once Ringotel confirms it. */
  private readonly delivered = new Set<string>();
  private readonly checked = new Set<string>();

  constructor(private smsSendService: SmsSendService) {}

  get current (): SmsChatRequest | null {
    return this.openSubject.value;
  }

  open (request: SmsChatRequest): void {
    this.openSubject.next(request);
  }

  close (): void {
    this.openSubject.next(null);
  }

  /** Asks Ringotel once per sent message whether it's been delivered. */
  checkDelivery (messages: CrmMessage[]): void {
    for (const m of messages) {
      if (m.channel !== 'sms' || m.direction !== 'outbound') continue;
      if (m.status !== 'sent' || !m.providerMessageId || this.checked.has(m.id)) continue;
      this.checked.add(m.id);

      this.smsSendService.status(m.providerMessageId).subscribe({
        next: (res: any) => {
          if (String(res?.status ?? '').toLowerCase().includes('deliver')) {
            this.delivered.add(m.id);
          }
        }
      });
    }
  }

  /**
   * The inbound messages counted as unread: the newest `unreadCount`
   * inbound ones (that's how the thread's counter is kept). Oldest first,
   * same order as `messages`.
   */
  unreadInbound (messages: CrmMessage[], unreadCount: number): CrmMessage[] {
    if (unreadCount <= 0) return [];
    const inbound = messages.filter((m) => m.direction === 'inbound');
    return inbound.slice(Math.max(0, inbound.length - unreadCount));
  }

  /** Inbound messages from `from` onwards - what "Mark unread from here" flags. */
  inboundFrom (messages: CrmMessage[], from: CrmMessage): CrmMessage[] {
    const start = messages.findIndex((m) => m.id === from.id);
    return messages.slice(Math.max(0, start)).filter((m) => m.direction === 'inbound');
  }

  /** True when `a` and `b` are the same phone number in any format. */
  sameNumber (a: string | null | undefined, b: string | null | undefined): boolean {
    const x = (a ?? '').replace(/\D/g, '').slice(-9);
    const y = (b ?? '').replace(/\D/g, '').slice(-9);
    return !!x && x === y;
  }

  /** Effective status - the stored one, upgraded to 'delivered' once Ringotel confirms. */
  statusOf (m: CrmMessage): string {
    if (m.direction === 'inbound') return 'received';
    if (this.delivered.has(m.id)) return 'delivered';
    return m.status ?? 'sent';
  }

  statusTone (m: CrmMessage): SmsStatusTone {
    const status = this.statusOf(m);
    if (status === 'received') return 'received';
    if (status === 'delivered') return 'delivered';
    if (status.startsWith('failed') || status === 'complained') return 'failed';
    if (status === 'sending' || status === 'queued') return 'pending';
    return 'sent';
  }

  statusLabel (m: CrmMessage): string {
    switch (this.statusOf(m)) {
      case 'received': return 'Received';
      case 'delivered': return 'Delivered';
      case 'sending': return 'Sending';
      case 'queued': return 'Queued';
      case 'failed':
      case 'failed_permanent': return 'Failed';
      case 'failed_temporary': return 'Failed (temporary)';
      case 'complained': return 'Reported';
      default: return 'Sent';
    }
  }

  segments (text: string, segmentSize: number): number {
    return text.length === 0 ? 1 : Math.ceil(text.length / Math.max(segmentSize, 1));
  }

  toMillis (value: any): number | null {
    if (!value) return null;
    if (typeof value?.toMillis === 'function') return value.toMillis();
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  }

  sentAtMillis (m: CrmMessage): number {
    return this.toMillis(m.sentAt) ?? Date.now();
  }

  formatNumber (raw: string | null | undefined): string {
    const value = raw ?? '';
    const digits = value.replace(/[^\d]/g, '');
    if (digits.length === 11 && digits.startsWith('61')) {
      return `+61 ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
    }
    if (digits.length === 10 && digits.startsWith('04')) {
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }
    if (digits.length === 10 && digits.startsWith('0')) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)} ${digits.slice(6)}`;
    }
    return value;
  }

  formatDateTime (millis: number | null): string {
    if (millis === null) return '—';
    return new Date(millis).toLocaleString([], {
      day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  }

  initials (name: string | null | undefined): string {
    const trimmed = name?.trim();
    if (!trimmed) return '';
    return trimmed.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('');
  }
}
